import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, TextField, Select, MenuItem, InputLabel, FormControl,
  IconButton, Table, TableHead, TableBody, TableRow, TableCell,TableContainer,
  Paper, Pagination, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import BASE_URL from '../config';

const PAGE_SIZE = 15;

export default function ConcessionPage() {
  const [courses, setCourses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);

  const [filters, setFilters] = useState({ courseId: '', academicYearId: '', classId: '', studentName: '' });
  const [concessions, setConcessions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [editing, setEditing] = useState(null); // { ConcessionID, ConcessionAmount, FinalAmount }
  const [confirmDelete, setConfirmDelete] = useState(null); // ConcessionID

  // Load courses
  useEffect(() => {
    axios.get(`${BASE_URL}/api/scourses`).then(r => setCourses(r.data)).catch(()=>{});
  }, []);

  // Load academic years & classes when course changes
  useEffect(() => {
    if (filters.courseId) {
      axios.get(`${BASE_URL}/api/sacademic-years/${filters.courseId}`).then(r => setAcademicYears(r.data)).catch(()=>setAcademicYears([]));
      axios.get(`${BASE_URL}/api/sclasses/${filters.courseId}`).then(r => setClasses(r.data)).catch(()=>setClasses([]));
    } else {
      setAcademicYears([]); setClasses([]);
    }
  }, [filters.courseId]);

  // Fetch concessions whenever filters or page change (no search button)
  useEffect(() => {
    const params = {
      courseID: filters.courseId || undefined,
      academicYearID: filters.academicYearId || undefined,
      classID: filters.classId || undefined,
      studentName: filters.studentName || undefined,
      page, pageSize: PAGE_SIZE
    };
    axios.get(`${BASE_URL}/api/concessions`, { params }).then(r => {
      setConcessions(r.data.rows);
      setTotalPages(Math.max(1, Math.ceil(r.data.total / PAGE_SIZE)));
    }).catch(()=>{ setConcessions([]); setTotalPages(1); });
  }, [filters, page]);

  const handleChange = (key) => (e) => { setFilters(prev => ({ ...prev, [key]: e.target.value })); setPage(1); };

  const handleEditOpen = (row) => {
    setEditing({ ...row });
  };
  const handleEditClose = () => setEditing(null);

  const handleSaveEdit = async () => {
    if (!editing) return;
    const { ConcessionID, ConcessionAmount, FinalAmount } = editing;
    if (parseFloat(ConcessionAmount) > parseFloat(FinalAmount)) {
      alert('ConcessionAmount should not be greater than Fee/FinalAmount');
      return;
    }
    try {
      await axios.put(`${BASE_URL}/api/concession/${ConcessionID}`, { ConcessionAmount,adminName: sessionStorage.getItem("adminName") });
      // refresh
      setFilters(f => ({ ...f }));
      setEditing(null);
      alert('Updated successfully');
    } catch (err) {
      console.error(err);
      alert('Update failed');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${BASE_URL}/api/concession/${confirmDelete}`, {
  data: { adminName: sessionStorage.getItem("adminName") }
}

      );
      setConfirmDelete(null);
      setFilters(f => ({ ...f }));
      alert('Successfully deleted');
    } catch (err) {
      console.error(err);
      alert('Delete failed');
    }
  };
return (
    <Box p={{ xs:2, md:4 }}>
      <Typography variant="h5" mb={2}>Concessions</Typography>
      <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, gap:2, mb:2 }}>
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Course</InputLabel>
          <Select value={filters.courseId} label="Course" onChange={handleChange('courseId')}>
            <MenuItem value="">All</MenuItem>
            {courses.map(c=> <MenuItem key={c.CourseID} value={c.CourseID}>{c.CourseName}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Academic Year</InputLabel>
          <Select value={filters.academicYearId} label="Academic Year" onChange={handleChange('academicYearId')}>
            <MenuItem value="">All</MenuItem>
            {academicYears.map(a=> <MenuItem key={a.AcademicYearID} value={a.AcademicYearID}>{a.AcademicYearName}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Class</InputLabel>
          <Select value={filters.classId} label="Class" onChange={handleChange('classId')}>
            <MenuItem value="">All</MenuItem>
            {classes.map(cl=> <MenuItem key={cl.ClassID} value={cl.ClassID}>{cl.ClassName}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Search Student"
          value={filters.studentName}
          onChange={handleChange('studentName')}
          sx={{ flex:1, minWidth:160 }}
        />
      </Box>

<TableContainer component={Paper} sx={{ maxHeight: 500 }}>
  <Table stickyHeader>
    <TableHead>
      <TableRow>
        {[
          "Date",
          "Academic Year",
          "Course",
          "Class",
          "Student",
          "Category (Route)",
          "Fees",
          "Concession",
          "Final Amount",
          "Actions",
        ].map((header, i) => (
          <TableCell
            key={i}
            align={i >= 6 && i < 9 ? "right" : i === 9 ? "center" : "left"}
            sx={{
              backgroundColor: "#1976d2", // header color
              color: "white",
              fontWeight: "bold",
              fontSize: "0.95rem",
            }}
          >
            {header}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>

    <TableBody>
      {concessions.map((row, index) => (
        <TableRow
          key={row.ConcessionID}
          sx={{
            backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff", // zebra
            "&:hover": {
              backgroundColor: "#e3f2fd", // hover effect
            },
          }}
        >
          <TableCell>{row.Date}</TableCell>
          <TableCell>{row.AcademicYearName}</TableCell>
          <TableCell>{row.CourseName}</TableCell>
          <TableCell>{row.ClassName}</TableCell>
          <TableCell>{row.StudentName}</TableCell>
          <TableCell>{row.CategoryName}</TableCell>
          <TableCell align="right">{parseFloat(row.Fees).toFixed(2)}</TableCell>
          <TableCell align="right">{parseFloat(row.ConcessionAmount).toFixed(2)}</TableCell>
          <TableCell align="right">{parseFloat(row.FinalAmount).toFixed(2)}</TableCell>
          <TableCell align="center">
            <IconButton
              size="small"
              onClick={() => handleEditOpen(row)}
              aria-label="edit"
              sx={{ color: "#1976d2" }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setConfirmDelete(row.ConcessionID)}
              aria-label="delete"
              sx={{ color: "red" }}
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>


      {/* <Paper sx={{ overflowX:'auto' }}>
        <Table>
          <TableHead sx={{ backgroundColor:'#f5f5f5' }}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Academic Year</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Category (Route)</TableCell>
              <TableCell>Fees</TableCell>
              <TableCell>Concession</TableCell>
              <TableCell>Final Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {concessions.map(row => (
              <TableRow key={row.ConcessionID}>
                <TableCell>{row.Date}</TableCell>
                <TableCell>{row.AcademicYearName}</TableCell>
                <TableCell>{row.CourseName}</TableCell>
                <TableCell>{row.ClassName}</TableCell>
                <TableCell>{row.StudentName}</TableCell>
                <TableCell>{row.CategoryName}</TableCell>
                <TableCell>{parseFloat(row.Fees).toFixed(2)}</TableCell>
                <TableCell>{parseFloat(row.ConcessionAmount).toFixed(2)}</TableCell>
                <TableCell>{parseFloat(row.FinalAmount).toFixed(2)}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEditOpen(row)} aria-label="edit">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => setConfirmDelete(row.ConcessionID)} aria-label="delete">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper> */}

      <Box mt={2} display="flex" justifyContent="center">
        <Pagination count={totalPages} page={page} onChange={(e,v)=>setPage(v)} />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onClose={handleEditClose} fullWidth>
        <DialogTitle>Edit Concession</DialogTitle>
        <DialogContent>
          {editing && (
            <Box sx={{ display:'flex', gap:2, mt:1 }}>
              <TextField label="Concession Amount" value={editing.ConcessionAmount}
                onChange={e=> setEditing(prev=> ({ ...prev, ConcessionAmount: e.target.value }))}
                fullWidth />
              <TextField label="Final Amount" value={editing.FinalAmount} disabled fullWidth />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onClose={()=>setConfirmDelete(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this concession?</DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}